import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertTriangle, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { WeekView } from "./WeekView";
import { MonthView } from "./MonthView";
import { ListView } from "./ListView";
import { CalendarLegend } from "./CalendarLegend";
import { LessonDetailDialog } from "./LessonDetailDialog";
import { LessonDialog } from "./LessonDialog";
import {
  teacherOverlaps, courseColorMap, canEditCourse, todayIso, addDays, addMonths, startOfWeek, monthStart, monthEnd, fmtDay, fmtMonth,
} from "./calendarUtils";

const VIEWS = [["week", "Settimana"], ["month", "Mese"], ["list", "Elenco"]];
const VIEW_KEY = "arete-cal-view";

// Data iniziale: oggi se dentro l'anno scolastico, altrimenti il 1° settembre dell'anno
const initialCursor = (year) => {
  const t = todayIso(); const y = parseInt(year, 10);
  return !y || (t >= `${y}-09-01` && t <= `${y + 1}-08-31`) ? t : `${y}-09-01`;
};

const periodOf = (view, cursor) => {
  if (view === "week") { const f = startOfWeek(cursor); return { from: f, to: addDays(f, 6) }; }
  if (view === "month") { const ms = monthStart(cursor); const f = startOfWeek(ms); return { from: f, to: addDays(startOfWeek(monthEnd(cursor)), 6) }; }
  return { from: monthStart(cursor), to: monthEnd(cursor) };
};

export const SchoolCalendar = ({ year, years, currentYear, onYear }) => {
  const { user } = useAuth();
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "week");
  const [cursor, setCursor] = useState(() => initialCursor(year));
  const [lessons, setLessons] = useState(null);
  const [base, setBase] = useState({ metas: [], closures: [] });
  const [course, setCourse] = useState("all");
  const [teacher, setTeacher] = useState("all");
  const [detail, setDetail] = useState(null);
  const [dlg, setDlg] = useState(null); // { lesson } spostamento | { course } recupero

  const period = periodOf(view, cursor);

  useEffect(() => {
    setCourse("all"); setTeacher("all"); setCursor(initialCursor(year));
    Promise.all([api.get("/staff/course-meta", { params: { year } }), api.get("/staff/closures", { params: { year } })])
      .then(([m, c]) => setBase({ metas: m.data, closures: c.data }))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)));
  }, [year]);

  useEffect(() => {
    let live = true;
    setLessons(null);
    api.get("/staff/lessons", { params: { year, date_from: period.from, date_to: period.to } })
      .then((r) => live && setLessons(r.data))
      .catch((err) => { if (live) { setLessons([]); toast.error(apiError(err.response?.data?.detail)); } });
    return () => { live = false; };
  }, [year, period.from, period.to]);

  const pickView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  const step = (n) => setCursor((c) => (view === "week" ? addDays(c, 7 * n) : addMonths(c, n)));

  const allCourses = base.metas.map((m) => m.course);
  const metaOf = (c) => base.metas.find((m) => m.course === c);
  const canEdit = (c) => canEditCourse(metaOf(c), user);
  const editableCourses = allCourses.filter(canEdit);
  const locked = new Set(base.metas.filter((m) => m.calendar_locked).map((m) => m.course));
  const colors = useMemo(() => courseColorMap(allCourses), [allCourses.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const teachers = [...new Set(base.metas.map((m) => m.teacher_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "it"));
  const overlaps = useMemo(() => teacherOverlaps(lessons || []), [lessons]);
  const shown = (lessons || []).filter((x) => (course === "all" || x.course === course) && (teacher === "all" || x.teacher_name === teacher));
  const shownOverlaps = shown.filter((x) => overlaps.has(x.id) && (view !== "month" || x.date.slice(0, 7) === cursor.slice(0, 7))).length;

  // Aggiornamenti locali: nessun ricaricamento degli iscritti
  const sortL = (ls) => [...ls].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  const inPeriod = (x) => x.date >= period.from && x.date <= period.to;
  const teacherOf = (c) => base.metas.find((m) => m.course === c)?.teacher_name || "";
  const put = (x) => {
    const full = { ...x, teacher_name: teacherOf(x.course) };
    setLessons((ls) => sortL([...(ls || []).filter((y) => y.id !== x.id), ...(inPeriod(full) ? [full] : [])]));
    setDetail((d) => (d && d.id === x.id ? full : d));
  };
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); } catch (err) { toast.error(apiError(err.response?.data?.detail)); throw err; } };

  const actions = {
    move: (x) => { setDetail(null); setDlg({ lesson: x }); },
    cancel: (x) => call(async () => put((await api.post(`/staff/lessons/${x.id}/cancel`)).data), "Lezione annullata").catch(() => {}),
    restore: (x) => call(async () => put((await api.post(`/staff/lessons/${x.id}/restore`)).data), "Lezione ripristinata").catch(() => {}),
    remove: (x) => call(async () => { await api.delete(`/staff/lessons/${x.id}`); setLessons((ls) => ls.filter((y) => y.id !== x.id)); setDetail(null); }, "Recupero eliminato").catch(() => {}),
    recovery: (c) => { setDetail(null); setDlg({ course: c }); },
  };
  const submitDlg = (v) => (dlg.lesson
    ? call(async () => put((await api.post(`/staff/lessons/${dlg.lesson.id}/move`, v)).data), "Lezione spostata")
    : call(async () => put((await api.post("/staff/lessons", v, { params: { year } })).data), "Recupero aggiunto"));

  const label = view === "week" ? `${fmtDay(period.from)} – ${fmtDay(period.to)}` : fmtMonth(cursor);
  const cOpts = [["all", "Tutti i corsi"], ...allCourses.map((c) => [c, c])];
  const tOpts = [["all", "Tutti i docenti"], ...teachers.map((t) => [t, t])];

  return (
    <div className="space-y-4" data-testid="school-calendar" lang="it">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Filter label="Anno scolastico" testid="school-cal-year" value={year} onChange={onYear} opts={years.map((y) => [y, `${y}${y === currentYear ? " (corrente)" : ""}`])} />
        <Filter label="Corso" testid="school-cal-course" value={course} onChange={setCourse} opts={cOpts} />
        <Filter label="Docente" testid="school-cal-teacher" value={teacher} onChange={setTeacher} opts={tOpts} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-border bg-card p-1" role="tablist" data-testid="cal-view-switch">
          {VIEWS.map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} data-testid={`cal-view-${v}`} onClick={() => pickView(v)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
        {editableCourses.length > 0 && <Button size="sm" variant="outline" data-testid="cal-add-recovery" onClick={() => setDlg({ course: editableCourses.includes(course) ? course : "" })}><Plus size={14} className="mr-1" /> Lezione di recupero</Button>}
      </div>

      <div className="flex items-center gap-2">
        <Button size="icon" variant="outline" data-testid="cal-prev" onClick={() => step(-1)} aria-label="Precedente"><ChevronLeft size={18} /></Button>
        <Button size="sm" variant="outline" data-testid="cal-today" onClick={() => setCursor(todayIso())}>Oggi</Button>
        <Button size="icon" variant="outline" data-testid="cal-next" onClick={() => step(1)} aria-label="Successivo"><ChevronRight size={18} /></Button>
        <h3 className="text-lg sm:text-xl font-semibold capitalize ml-1" data-testid="cal-period-label">{label}</h3>
      </div>

      <CalendarLegend courses={allCourses} colors={colors} locked={locked} />

      {shownOverlaps > 0 && (
        <p data-testid="school-cal-overlap-alert" className="text-sm rounded-lg bg-amber-500/15 text-amber-900 p-3 flex gap-2"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {shownOverlaps} lezioni in questo periodo hanno lo stesso docente in orari sovrapposti: sono segnalate con il triangolo.</p>
      )}

      {lessons === null ? <p className="text-muted-foreground" data-testid="cal-loading">Caricamento…</p>
        : view === "week" ? <WeekView from={period.from} lessons={shown} colors={colors} overlaps={overlaps} closures={base.closures} onOpen={setDetail} />
          : view === "month" ? <MonthView key={cursor.slice(0, 7)} gridFrom={period.from} gridTo={period.to} month={cursor.slice(0, 7)} lessons={shown} colors={colors} overlaps={overlaps} closures={base.closures} onOpen={setDetail} />
            : <ListView lessons={shown} colors={colors} overlaps={overlaps} onOpen={setDetail} />}

      <LessonDetailDialog x={detail} color={detail ? colors[detail.course] : undefined} overlap={detail ? overlaps.has(detail.id) : false} onClose={() => setDetail(null)} actions={actions}
        canEdit={detail ? canEdit(detail.course) : false} locked={detail ? locked.has(detail.course) : false} />
      <LessonDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} lesson={dlg?.lesson || null} closures={base.closures} onSubmit={submitDlg}
        courses={dlg && !dlg.lesson ? editableCourses : undefined} defaultCourse={dlg?.course || ""} />
    </div>
  );
};

const Filter = ({ label, testid, value, onChange, opts }) => (
  <div><label className="text-xs text-muted-foreground">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white h-auto min-h-10 text-left whitespace-normal"><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);
